import { HOUR } from 'core/common/constants';
import { Config } from 'core/common/contexts';
import { RequestLimitExceededError } from 'core/common/errors';
import { HttpClient } from 'core/common/services';
import { FunnelSettingsServiceInterface } from 'core/common/services/funnel-settings';
import { Logger } from 'core/common/services/logger/interfaces';
import { delay } from 'core/common/utils/delay';
import {
  paymentActiveProvidersDefaultValue,
  paymentActiveProvidersFeature,
} from 'core/feature-flags/config';
import { FeatureFlags } from 'core/feature-flags/services';
import { LocationService } from 'core/user/services';
import {
  CreateOrderParams,
  CreatePaymentParams,
  CreateRecurringPaymentPayload,
  Order,
  OrderQuiz,
  Payment,
  PaymentFormData,
  PaymentMethod,
  PaymentOrderStatus,
  PaymentStatus,
  PaymentType,
  RecurringInitiator,
  RoutingParams,
  UpdateOrderParams,
} from '../../entities';
import {
  OrderDtoMapper,
  OrderQuizDtoMapper,
  PaymentDtoMapper,
  PaymentFormDataDtoMapper,
  PaymentStatusDtoMapper,
} from '../../mappers';
import {
  CreateRecurringPaymentDto,
  OrderDto,
  PaymentDto,
  PaymentFormDataDto,
  PaymentStatusDto,
} from '../dto';

export class PaymentService {
  constructor(
    readonly httpClient: HttpClient,
    readonly logger: Logger,
    private readonly funnelSettingsService: FunnelSettingsServiceInterface,
    private readonly locationService: LocationService,
    private readonly featureFlags: FeatureFlags,
    private readonly config: Config,
  ) {}

  async createOrder(payload: CreateOrderParams): Promise<Order> {
    const dtoMapper = new OrderDtoMapper();

    const { data } = await this.httpClient.post<OrderDto>('/payment-gateway/orders', {
      data: {
        ...payload,
        routing_params: this.getRoutingParams(),
      },
    });

    return dtoMapper.toEntity(data);
  }

  async createPayment(params: CreatePaymentParams): Promise<PaymentFormData> {
    const { offerId, email, orderId } = params;
    const dtoMapper = new PaymentFormDataDtoMapper();

    const { paymentForm } = this.featureFlags.getFeatureValue(
      paymentActiveProvidersFeature,
      paymentActiveProvidersDefaultValue,
    );

    const { data } = await this.httpClient.post<PaymentFormDataDto>(`/payment-gateway/payments`, {
      data: {
        email,
        offer_id: offerId,
        methods: [
          ...(paymentForm.card ? ['card'] : []),
          ...(paymentForm.paypal ? ['paypal'] : []),
          ...(paymentForm.google_pay ? ['google-pay'] : []),
        ],
        language: this.funnelSettingsService.getFunnelLocale(),
        metadata: {
          flow: this.funnelSettingsService.getFlowName(),
          order_id: orderId,
        },
        routing_params: this.getRoutingParams(),
      },
    });

    return dtoMapper.toEntity(data);
  }

  async updateOrder(params: UpdateOrderParams): Promise<void> {
    const { orderId, offerId, extra_data } = params;

    await this.httpClient.patch(`/payment-gateway/orders/${orderId}`, {
      data: {
        ...(offerId && { offer_id: offerId }),
        extra_data,
        routing_params: this.getRoutingParams(),
      },
    });
  }

  async getPayments(params: {
    offerId: string;
    email: string;
    createdAfter?: number;
    status?: PaymentOrderStatus | Array<PaymentOrderStatus>;
  }): Promise<Array<Payment>> {
    const {
      offerId,
      email,
      createdAfter = Date.now() - HOUR,
      status = this.config.activePaymentStatuses,
    } = params;

    const { data } = await this.httpClient.get<Array<PaymentDto>>('/payment-service/payments', {
      params: {
        email,
        status,
        offer_id: offerId,
        created_after: createdAfter,
      },
    });

    const dtoMapper = new PaymentDtoMapper();

    return data.map(dtoMapper.toEntity);
  }

  async getPaymentMethods(paymentId: string): Promise<PaymentMethod> {
    const { paymentMethod } = await this.waitPaymentStatus(paymentId);

    return paymentMethod;
  }

  async getOrder(orderId: string, params: Record<string, string> = {}): Promise<OrderQuiz> {
    const dtoMapper = new OrderQuizDtoMapper();

    const { data } = await this.httpClient.get<OrderDto>(`/payment-gateway/orders/${orderId}`, {
      params,
    });

    return dtoMapper.toEntity(data);
  }

  async getPaymentStatus(paymentId: string): Promise<PaymentStatus> {
    const dtoMapper = new PaymentStatusDtoMapper();

    const { data } = await this.httpClient.get<PaymentStatusDto>(
      `/payment-service/payments/${paymentId}/status`,
    );

    return dtoMapper.toEntity(data);
  }

  async createRecurringPayment(payload: CreateRecurringPaymentPayload) {
    const { email, offerId, orderId, paymentType } = payload;
    try {
      const { data } = await this.httpClient.post<CreateRecurringPaymentDto>(
        '/payment-gateway/payments/recurring/by-latest',
        {
          data: {
            email,
            offer_id: offerId,
            initiator: this.getRecurringInitiator(paymentType),
            metadata: {
              order_id: orderId,
            },
            routing_params: this.getRoutingParams(),
          },
        },
      );

      const result = await this.waitPaymentStatus(data.payment_id);

      return result;
    } catch (err) {
      throw err;
    }
  }

  private async waitPaymentStatus(paymentId: string): Promise<PaymentStatus> {
    const { recurringPayment } = this.config;
    const { refetchDelay, maxRetryAttempts } = recurringPayment;

    for (let attempt = 0; attempt < maxRetryAttempts; attempt++) {
      const result = await this.getPaymentStatus(paymentId);

      if (result.status !== PaymentOrderStatus.PROCESSING) {
        return result;
      }

      await delay(refetchDelay);
    }

    throw new RequestLimitExceededError();
  }

  private getRoutingParams(): RoutingParams {
    return {
      project: 'appnebula',
      funnel: this.funnelSettingsService.getFunnelName(),
      domain: this.config.mainAppUrl,
      country: this.locationService.getCountry(),
    };
  }

  private getRecurringInitiator(paymentType: PaymentType | null): RecurringInitiator {
    if (paymentType === PaymentType.APPLE_PAY || paymentType === PaymentType.GOOGLE_PAY) {
      return RecurringInitiator.MERCHANT;
    }
    return RecurringInitiator.CUSTOMER;
  }
}
